import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FileSearch,
  AlertCircle,
  Loader2,
  Download,
  Filter,
  Eye,
  AlertTriangle,
  Info,
  Shield,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface AuditLog {
  id: string
  admin_user_id: string
  admin_email: string
  admin_name: string
  action_type: string
  action_category: string
  target_church_id: string | null
  target_church_name: string | null
  action_details: any
  before_value: any
  after_value: any
  ip_address: string | null
  user_agent: string | null
  severity: 'info' | 'warning' | 'critical'
  created_at: string
}

interface PlatformAuditLogProps {
  user: AdminUser | null
}

export default function PlatformAuditLog({ user }: PlatformAuditLogProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([])
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [severityFilter, setSeverityFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    critical: 0,
    warnings: 0,
  })

  // Check if user is super admin
  if (user?.adminLevel !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to access platform audit logs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    loadAuditLogs()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [logs, searchTerm, categoryFilter, severityFilter, dateFrom, dateTo])

  async function loadAuditLogs() {
    try {
      setLoading(true)

      const { data, error } = await supabase
        .from('platform_audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500) // Last 500 events

      if (error) throw error

      setLogs(data || [])
      calculateStats(data || [])
    } catch (error) {
      console.error('Error loading audit logs:', error)
    } finally {
      setLoading(false)
    }
  }

  function calculateStats(logData: AuditLog[]) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const todayLogs = logData.filter(log => new Date(log.created_at) >= today)
    const criticalLogs = logData.filter(log => log.severity === 'critical')
    const warningLogs = logData.filter(log => log.severity === 'warning')

    setStats({
      total: logData.length,
      today: todayLogs.length,
      critical: criticalLogs.length,
      warnings: warningLogs.length,
    })
  }

  function applyFilters() {
    let filtered = [...logs]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.admin_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.admin_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.action_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.target_church_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(log => log.action_category === categoryFilter)
    }

    // Severity filter
    if (severityFilter !== 'all') {
      filtered = filtered.filter(log => log.severity === severityFilter)
    }

    // Date filters
    if (dateFrom) {
      filtered = filtered.filter(log => new Date(log.created_at) >= new Date(dateFrom))
    }
    if (dateTo) {
      const endDate = new Date(dateTo)
      endDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter(log => new Date(log.created_at) <= endDate)
    }

    setFilteredLogs(filtered)
  }

  function getSeverityBadge(severity: string) {
    switch (severity) {
      case 'critical':
        return <Badge className="bg-red-500"><AlertCircle className="h-3 w-3 mr-1" />Critical</Badge>
      case 'warning':
        return <Badge className="bg-yellow-500"><AlertTriangle className="h-3 w-3 mr-1" />Warning</Badge>
      default:
        return <Badge variant="outline"><Info className="h-3 w-3 mr-1" />Info</Badge>
    }
  }

  function formatActionType(actionType: string) {
    return actionType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  function handleViewDetails(log: AuditLog) {
    setSelectedLog(log)
    setShowDetailsDialog(true)
  }

  async function handleExport() {
    const csv = generateCSV(filteredLogs)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  function generateCSV(data: AuditLog[]) {
    const headers = ['Date', 'Admin Email', 'Action Type', 'Category', 'Church', 'Severity', 'Details']
    const rows = data.map(log => [
      new Date(log.created_at).toLocaleString(),
      log.admin_email,
      formatActionType(log.action_type),
      log.action_category || '',
      log.target_church_name || '',
      log.severity,
      JSON.stringify(log.action_details || {})
    ])

    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading audit logs...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <FileSearch className="h-8 w-8" />
            Platform Audit Log
          </h1>
          <p className="text-muted-foreground mt-1">
            Complete audit trail of all platform admin actions
          </p>
        </div>
        <Button onClick={handleExport} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Last 500 events</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.today}</div>
            <p className="text-xs text-muted-foreground">Events today</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Critical
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{stats.critical}</div>
            <p className="text-xs text-muted-foreground">Require review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Warnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">{stats.warnings}</div>
            <p className="text-xs text-muted-foreground">Need attention</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <Input
              placeholder="Search admin, action, church..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="church_management">Church Management</SelectItem>
                <SelectItem value="subscription">Subscription</SelectItem>
                <SelectItem value="payment">Payment</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="settings">Settings</SelectItem>
              </SelectContent>
            </Select>

            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              placeholder="From date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />

            <Input
              type="date"
              placeholder="To date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          {(searchTerm || categoryFilter !== 'all' || severityFilter !== 'all' || dateFrom || dateTo) && (
            <div className="mt-3 flex items-center gap-2">
              <p className="text-sm text-muted-foreground">
                Showing {filteredLogs.length} of {logs.length} events
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('')
                  setCategoryFilter('all')
                  setSeverityFilter('all')
                  setDateFrom('')
                  setDateTo('')
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardHeader>
          <CardTitle>Audit Events</CardTitle>
          <CardDescription>
            {filteredLogs.length} events matching your filters
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Admin User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Target Church</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No audit logs found matching your filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-sm">
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{log.admin_name || 'Unknown'}</div>
                          <div className="text-xs text-muted-foreground">{log.admin_email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{formatActionType(log.action_type)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.action_category || 'N/A'}</Badge>
                      </TableCell>
                      <TableCell>
                        {log.target_church_name || <span className="text-muted-foreground">N/A</span>}
                      </TableCell>
                      <TableCell>
                        {getSeverityBadge(log.severity)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(log)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Audit Log Details
            </DialogTitle>
            <DialogDescription>
              Complete details of this audit event
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Timestamp</p>
                  <p className="font-mono">{new Date(selectedLog.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Severity</p>
                  <div className="mt-1">{getSeverityBadge(selectedLog.severity)}</div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Admin User</p>
                  <p>{selectedLog.admin_name || 'Unknown'}</p>
                  <p className="text-sm text-muted-foreground">{selectedLog.admin_email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Action Type</p>
                  <p className="font-medium">{formatActionType(selectedLog.action_type)}</p>
                </div>
                {selectedLog.target_church_name && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Target Church</p>
                    <p>{selectedLog.target_church_name}</p>
                  </div>
                )}
                {selectedLog.action_category && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Category</p>
                    <Badge variant="outline">{selectedLog.action_category}</Badge>
                  </div>
                )}
              </div>

              {/* Action Details */}
              {selectedLog.action_details && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Action Details</p>
                  <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.action_details, null, 2)}
                  </pre>
                </div>
              )}

              {/* Before/After Values */}
              {(selectedLog.before_value || selectedLog.after_value) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedLog.before_value && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">Before</p>
                      <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                        {JSON.stringify(selectedLog.before_value, null, 2)}
                      </pre>
                    </div>
                  )}
                  {selectedLog.after_value && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">After</p>
                      <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                        {JSON.stringify(selectedLog.after_value, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Technical Details */}
              {(selectedLog.ip_address || selectedLog.user_agent) && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Technical Details</p>
                  <div className="space-y-2">
                    {selectedLog.ip_address && (
                      <div>
                        <span className="text-sm text-muted-foreground">IP Address: </span>
                        <span className="font-mono text-sm">{selectedLog.ip_address}</span>
                      </div>
                    )}
                    {selectedLog.user_agent && (
                      <div>
                        <span className="text-sm text-muted-foreground">User Agent: </span>
                        <span className="font-mono text-xs">{selectedLog.user_agent}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
